/**
 * Cut check for die-cut magnets, shared by the editors.
 *
 *   MDCut.analyze(art, inchesW)          how the logo would be cut at its real size
 *   MDCut.fixVersions(opts)              AI redraws under the logo maker's print rules
 *   MDCut.variants(art, inchesW, report) contour versions, the fallback when no redraw
 *   MDCut.render(art, inchesW, variant, opts)  print artwork with a white contour
 *
 * The cutter follows the outline of the printed artwork. Loose letters,
 * specks and hairlines become pieces too small to cut or to stick. Those
 * logos are redrawn by the AI with heavier strokes and solid shapes; if
 * that isn't possible, a white contour joins the pieces and the artwork
 * itself is printed untouched.
 *
 * Limits come from the supplier and are kept in one place so they can be
 * tuned without touching the logic.
 */
(function () {
  'use strict';

  var LIMITS = {
    MAX_PIECES: 3,         /* contour versions with more pieces than this aren't offered */
    MIN_PIECE_IN: 1.0,     /* a piece must be at least this wide and tall */
    MIN_LINE_IN: 0.25,     /* strokes thinner than this tear when cut */
    MAX_THIN_SQIN: 0.6,    /* tolerated total area of too-thin parts */
    EDGE_IN: 0.55,         /* contour added around the artwork */
    TIGHT_EDGE_IN: 0.3,
    MAX_BRIDGE_IN: 7,      /* widest gap the one-piece contour may close */
    SMOOTH_IN: 1.0,
    NECK_IN: 1.0           /* joins narrower than twice this would snap */
  };
  var PPI = 14;            /* analysis resolution, pixels per real inch */
  var INF = 1e20;

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }

  /* ── Binary images at analysis resolution ── */

  function grid(art, inchesW, padIn) {
    var inchesH = inchesW * art.height / art.width;
    var pad = Math.ceil(padIn * PPI) + 2;
    var iw = Math.max(1, Math.round(inchesW * PPI)), ih = Math.max(1, Math.round(inchesH * PPI));
    var w = iw + pad * 2, h = ih + pad * 2;
    var c = makeCanvas(w, h), g = c.getContext('2d');
    g.drawImage(art, pad, pad, iw, ih);
    var p = g.getImageData(0, 0, w, h).data, m = new Uint8Array(w * h);
    for (var i = 0; i < m.length; i++) m[i] = p[i * 4 + 3] > 90 ? 1 : 0;
    return { w: w, h: h, pad: pad, iw: iw, ih: ih, inchesW: inchesW, inchesH: inchesH, mask: m };
  }

  /* Squared Euclidean distance to the nearest set pixel (Felzenszwalb). */
  function edt(bin, w, h, invert) {
    var n = Math.max(w, h), f = new Float64Array(n), d = new Float64Array(n);
    var v = new Int32Array(n), z = new Float64Array(n + 1), out = new Float64Array(w * h);
    function pass(len) {
      var k = 0; v[0] = 0; z[0] = -INF; z[1] = INF;
      for (var q = 1; q < len; q++) {
        var s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
        while (s <= z[k]) { k--; s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
        k++; v[k] = q; z[k] = s; z[k + 1] = INF;
      }
      k = 0;
      for (q = 0; q < len; q++) {
        while (z[k + 1] < q) k++;
        d[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
      }
    }
    var x, y;
    for (x = 0; x < w; x++) {
      for (y = 0; y < h; y++) f[y] = (bin[y * w + x] ? !invert : invert) ? 0 : INF;
      pass(h);
      for (y = 0; y < h; y++) out[y * w + x] = d[y];
    }
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) f[x] = out[y * w + x];
      pass(w);
      for (x = 0; x < w; x++) out[y * w + x] = d[x];
    }
    return out;
  }

  function dilate(bin, w, h, rPx) {
    var d = edt(bin, w, h, false), r2 = rPx * rPx, out = new Uint8Array(w * h);
    for (var i = 0; i < out.length; i++) out[i] = d[i] <= r2 ? 1 : 0;
    return out;
  }

  function erode(bin, w, h, rPx) {
    var d = edt(bin, w, h, true), r2 = rPx * rPx, out = new Uint8Array(w * h);
    for (var i = 0; i < out.length; i++) out[i] = bin[i] && d[i] > r2 ? 1 : 0;
    return out;
  }

  function close(bin, w, h, rPx) { return rPx > 0 ? erode(dilate(bin, w, h, rPx), w, h, rPx) : bin; }
  function open(bin, w, h, rPx) { return rPx > 0 ? dilate(erode(bin, w, h, rPx), w, h, rPx) : bin; }

  /* 8-connected pieces with their size. */
  function pieces(bin, w, h) {
    var lab = new Int32Array(w * h), list = [], stack = new Int32Array(w * h);
    for (var s = 0; s < bin.length; s++) {
      if (!bin[s] || lab[s]) continue;
      var id = list.length + 1, top = 0, area = 0;
      var x0 = w, y0 = h, x1 = 0, y1 = 0;
      stack[top++] = s; lab[s] = id;
      while (top) {
        var j = stack[--top], x = j % w, y = (j / w) | 0;
        area++;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
        for (var dy = -1; dy <= 1; dy++) {
          var yy = y + dy; if (yy < 0 || yy >= h) continue;
          for (var dx = -1; dx <= 1; dx++) {
            var xx = x + dx; if (xx < 0 || xx >= w) continue;
            var k = yy * w + xx;
            if (bin[k] && !lab[k]) { lab[k] = id; stack[top++] = k; }
          }
        }
      }
      list.push({ area: area, x0: x0, y0: y0, x1: x1, y1: y1 });
    }
    return { list: list, labels: lab };
  }

  /* Everything not reachable from the border through empty space is a hole. */
  function fillHoles(bin, w, h) {
    var out = new Uint8Array(w * h), seen = new Uint8Array(w * h), stack = [];
    for (var x = 0; x < w; x++) stack.push(x, (h - 1) * w + x);
    for (var y = 0; y < h; y++) stack.push(y * w, y * w + w - 1);
    while (stack.length) {
      var j = stack.pop();
      if (seen[j] || bin[j]) continue;
      seen[j] = 1;
      var cx = j % w, cy = (j / w) | 0;
      if (cx > 0) stack.push(j - 1);
      if (cx < w - 1) stack.push(j + 1);
      if (cy > 0) stack.push(j - w);
      if (cy < h - 1) stack.push(j + w);
    }
    for (var i = 0; i < out.length; i++) out[i] = seen[i] ? 0 : 1;
    return out;
  }

  /* ── Analysis ── */

  function analyze(art, inchesW) {
    var G = grid(art, inchesW, 1);
    var w = G.w, h = G.h, m = G.mask;
    var P = pieces(m, w, h);
    var minPx = LIMITS.MIN_PIECE_IN * PPI;
    var tiny = P.list.filter(function (p) {
      return (p.x1 - p.x0 + 1) < minPx || (p.y1 - p.y0 + 1) < minPx;
    });
    var opened = open(m, w, h, LIMITS.MIN_LINE_IN * PPI / 2);
    var thinPx = 0;
    for (var i = 0; i < m.length; i++) if (m[i] && !opened[i]) thinPx++;
    var thinSqIn = thinPx / (PPI * PPI);
    var holes = pieces(fillHoles(m, w, h).map(function (v, k) { return v && !m[k] ? 1 : 0; }), w, h).list
      .filter(function (p) { return p.area > 1 && ((p.x1 - p.x0 + 1) < minPx || (p.y1 - p.y0 + 1) < minPx); });
    var problems = [];
    if (tiny.length) problems.push('tiny');
    if (thinSqIn > LIMITS.MAX_THIN_SQIN) problems.push('thin');
    return {
      ok: !problems.length,
      problems: problems,
      pieces: P.list.length,
      tiny: tiny.length,
      smallHoles: holes.length,
      thinSqIn: Math.round(thinSqIn * 10) / 10,
      grid: G
    };
  }

  /* Contour that wraps the artwork. bridgeIn closes gaps up to twice that
     width; edgeIn is the band of magnet left around the artwork. */
  /* The shape's sturdy core: what is left once every neck narrower than
     twice NECK_IN is removed. A shape is one solid piece only if its core is. */
  function contour(G, bridgeIn, edgeIn) {
    var w = G.w, h = G.h;
    var s = close(G.mask, w, h, bridgeIn * PPI);
    s = dilate(s, w, h, edgeIn * PPI);
    s = fillHoles(s, w, h);
    s = close(s, w, h, LIMITS.SMOOTH_IN * PPI);
    var core = open(s, w, h, LIMITS.NECK_IN * PPI);
    var hug = dilate(G.mask, w, h, edgeIn * PPI);
    var out = new Uint8Array(w * h);
    for (var i = 0; i < out.length; i++) out[i] = core[i] || hug[i] ? 1 : 0;
    return { shape: fillHoles(out, w, h), core: core };
  }

  function countPieces(s, w, h) { return pieces(s, w, h).list.length; }

  function solid(c, G) {
    return countPieces(c.core, G.w, G.h) === 1 && countPieces(c.shape, G.w, G.h) === 1;
  }

  /* Smallest bridge that makes the contour one solid piece. */
  function onePiece(G) {
    var lo = 0, hi = LIMITS.MAX_BRIDGE_IN, best = null;
    var c = contour(G, 0, LIMITS.EDGE_IN);
    if (solid(c, G)) return { shape: c.shape, bridgeIn: 0 };
    for (var it = 0; it < 7; it++) {
      var mid = (lo + hi) / 2;
      c = contour(G, mid, LIMITS.EDGE_IN);
      if (solid(c, G)) { best = { shape: c.shape, bridgeIn: mid }; hi = mid; }
      else lo = mid;
    }
    if (!best) {
      c = contour(G, LIMITS.MAX_BRIDGE_IN, LIMITS.EDGE_IN);
      best = { shape: c.shape, bridgeIn: LIMITS.MAX_BRIDGE_IN };
    }
    return best;
  }

  function variants(art, inchesW, report) {
    var G = grid(art, inchesW, LIMITS.MAX_BRIDGE_IN + 1);
    var out = [];
    var one = onePiece(G);
    out.push({ key: 'one', name: 'One piece', sub: 'A contour that joins everything', shape: one.shape, G: G, pieces: 1 });
    var tight = contour(G, 0.5, LIMITS.TIGHT_EDGE_IN).shape;
    var tp = pieces(tight, G.w, G.h).list;
    var minPx = LIMITS.MIN_PIECE_IN * PPI;
    var tightOk = tp.length > 1 && tp.length <= LIMITS.MAX_PIECES && tp.every(function (p) {
      return (p.x1 - p.x0 + 1) >= minPx && (p.y1 - p.y0 + 1) >= minPx;
    });
    if (tightOk) out.push({ key: 'tight', name: tp.length + ' pieces', sub: 'Slim edge, closer to your logo', shape: tight, G: G, pieces: tp.length });
    return out;
  }

  /* ── Print artwork ── */

  function maskCanvas(bin, w, h, colour) {
    var c = makeCanvas(w, h), g = c.getContext('2d'), d = g.createImageData(w, h);
    var rgb = parseInt(colour.slice(1), 16);
    for (var i = 0; i < bin.length; i++) {
      if (!bin[i]) continue;
      d.data[i * 4] = rgb >> 16; d.data[i * 4 + 1] = (rgb >> 8) & 255; d.data[i * 4 + 2] = rgb & 255; d.data[i * 4 + 3] = 255;
    }
    g.putImageData(d, 0, 0);
    return c;
  }

  /* Upscaled masks are blurred a little and re-thresholded so the cut line
     is smooth at print size instead of following the analysis pixels. */
  function upscale(src, W, H) {
    var c = makeCanvas(W, H), g = c.getContext('2d');
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.filter = 'blur(' + Math.max(1, W / src.width * 0.6) + 'px)';
    g.drawImage(src, 0, 0, W, H);
    g.filter = 'none';
    var d = g.getImageData(0, 0, W, H), p = d.data;
    for (var i = 3; i < p.length; i += 4) {
      var a = p[i];
      p[i] = a < 100 ? 0 : a > 156 ? 255 : Math.round((a - 100) * 255 / 56);
    }
    g.putImageData(d, 0, 0);
    return c;
  }

  /* opts.cutLine: draw the cutter path in magenta, as on a supplier proof. */
  function render(art, inchesW, variant, opts) {
    opts = opts || {};
    var G = variant.G;
    var k = Math.min(art.width / G.iw, (opts.maxPx || 2400) / G.w);
    var W = Math.round(G.w * k), H = Math.round(G.h * k);
    var out = makeCanvas(W, H), g = out.getContext('2d');
    var w = G.w, h = G.h, s = variant.shape;

    if (opts.cutLine) {
      var line = upscale(maskCanvas(dilate(s, w, h, 0.9), w, h, '#e6007e'), W, H);
      g.drawImage(line, 0, 0);
    }
    g.drawImage(upscale(maskCanvas(s, w, h, '#ffffff'), W, H), 0, 0);
    g.drawImage(art, G.pad * k, G.pad * k, G.iw * k, G.ih * k);
    return trim(out);
  }

  function trim(c) {
    return window.MDMockup && MDMockup.alphaTrim ? MDMockup.alphaTrim(c) : c;
  }

  /* ── AI redraw ──
     Same protection as the logo maker: an invisible Turnstile check earns a
     short-lived pass, and the server caps fixes per visitor and per day. */
  var ts = { id: null, wait: null };

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    return new Promise(function (resolve) {
      var sc = document.createElement('script');
      sc.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      sc.async = true;
      sc.onload = function () { resolve(window.turnstile || null); };
      sc.onerror = function () { resolve(null); };
      document.head.appendChild(sc);
    });
  }

  /* The box stays visible: Turnstile only draws in it when it needs the
     visitor to tick a box, which happens more inside in-app browsers. */
  function getPass(api, sitekey) {
    if (!sitekey) return Promise.resolve('');
    var token = loadTurnstile().then(function (T) {
      if (!T) return '';
      return new Promise(function (resolve) {
        ts.wait = resolve;
        if (ts.id === null) {
          var box = document.createElement('div');
          box.className = 'md-cut-ts';
          box.style.cssText = 'position:fixed;left:50%;bottom:16px;transform:translateX(-50%);z-index:2147483000';
          document.body.appendChild(box);
          ts.id = T.render(box, {
            sitekey: sitekey, execution: 'execute', appearance: 'interaction-only',
            callback: function (t) { if (ts.wait) { ts.wait(t); ts.wait = null; } },
            'error-callback': function () { if (ts.wait) { ts.wait(''); ts.wait = null; } }
          });
        } else T.reset(ts.id);
        T.execute(ts.id);
      });
    });
    var timeout = new Promise(function (r) { setTimeout(function () { r(''); }, 30000); });
    return Promise.race([token, timeout]).then(function (t) {
      if (!t) return '';
      return fetch(api + '/logo-pass', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: t }) })
        .then(function (r) { return r.ok ? r.json() : {}; })
        .then(function (d) { return d.pass || ''; })
        .catch(function () { return ''; });
    });
  }

  /* Asks for every version at once with one pass. onResult(i, url) runs as
     each arrives (url null when that one failed or took too long); the
     promise settles once all have. */
  var FIX_COUNT = 3, FIX_TIMEOUT_MS = 90000;
  /* Same order as the server's versions. */
  var FIX_NAMES = [
    { name: 'One piece', sub: 'Everything joined in a single magnet' },
    { name: 'Two pieces', sub: 'Symbol and name cut separately' },
    { name: 'With background', sub: 'Your logo on a solid shape' }
  ];

  /* The logo's own colours, strongest first, white left out: the redraw is
     told to use exactly these so it never drifts to a similar shade. Black
     and dark greys only count when they are a real part of the logo, not a
     thin drop shadow behind it. */
  function logoColors(img) {
    var M = window.MDMockup;
    var art = M ? M.prepareLogo(img, { removeBg: true, colour: 'original', max: 160 }) : img;
    var g = art.getContext ? art.getContext('2d') : null;
    if (!g) return [];
    var p = g.getImageData(0, 0, art.width, art.height).data, bins = {}, total = 0;
    for (var i = 0; i < p.length; i += 4) {
      if (p[i + 3] < 200) continue;
      if (p[i] > 235 && p[i + 1] > 235 && p[i + 2] > 235) continue;
      total++;
      var key = (p[i] >> 5) + ',' + (p[i + 1] >> 5) + ',' + (p[i + 2] >> 5);
      var e = bins[key] || (bins[key] = { n: 0, r: 0, g: 0, b: 0 });
      e.n++; e.r += p[i]; e.g += p[i + 1]; e.b += p[i + 2];
    }
    var out = [];
    Object.keys(bins).map(function (k) { return bins[k]; })
      .sort(function (a, b) { return b.n - a.n; })
      .forEach(function (e) {
        var c = [e.r / e.n, e.g / e.n, e.b / e.n];
        var near = out.some(function (o) { return Math.abs(o[0] - c[0]) + Math.abs(o[1] - c[1]) + Math.abs(o[2] - c[2]) < 90; });
        var mx = Math.max(c[0], c[1], c[2]), mn = Math.min(c[0], c[1], c[2]);
        var darkNeutral = mx < 70 || (mx < 110 && (mx - mn) / mx < 0.25);
        var share = e.n / Math.max(1, total);
        if (!near && out.length < 3 && share > (darkNeutral ? 0.25 : 0.04)) out.push(c);
      });
    return out.map(function (c) {
      return '#' + c.map(function (v) { return ('0' + Math.round(v).toString(16)).slice(-2); }).join('');
    });
  }

  function fixOne(api, body) {
    var ctl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, FIX_TIMEOUT_MS);
    return fetch(api + '/logo-fix', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctl ? ctl.signal : undefined
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return (d && d.url) || null; })
      .catch(function () { return null; })
      .then(function (url) { clearTimeout(timer); return url; });
  }

  function fixVersions(opts) {
    if (!opts || !opts.api || !opts.logoUrl) return Promise.resolve([]);
    return getPass(opts.api, opts.sitekey).then(function (pass) {
      var jobs = [], which = opts.versions || [0, 1, 2];
      which.forEach(function (i) {
        (function (i) {
          jobs.push(fixOne(opts.api, { logoUrl: opts.logoUrl, reason: opts.reason || '', version: i, colors: opts.colors || [], pass: pass }).then(function (url) {
            if (opts.onResult) opts.onResult(i, url);
            return url;
          }));
        })(i);
      });
      return Promise.all(jobs);
    });
  }

  window.MDCut = {
    LIMITS: LIMITS,
    analyze: analyze,
    FIX_COUNT: FIX_COUNT,
    FIX_NAMES: FIX_NAMES,
    logoColors: logoColors,
    fixVersions: fixVersions,
    variants: variants,
    render: render
  };
})();
